import type { SluggedId } from "Models";
import { AdKitKey } from "types/enums";
import { z } from "zod";
import { DateTime } from "luxon";

const sluggedIdRegex =
  /^[0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz]{8}$/;

// eslint-disable-next-line no-control-regex -- Intentional: validating Windows file names which disallow control characters
export const windowsFileNameXmlRegex =
  /^(?=.{1,100}$)(?!.*[<>:"/\\|?*\x00-\x1F]).+\.xml$/;

// eslint-disable-next-line no-control-regex -- Intentional: validating Windows file names which disallow control characters
export const windowsFileNameImageRegex =
  /^(?=.{1,100}$)(?!.*[<>:"/\\|?*\x00-\x1F]).+\.(jpg|jpeg|png|bmp)$/;
export type AdKitKeyType = `${AdKitKey}` | keyof typeof AdKitKey;

export const DEFAULT_DATETIME = DateTime.fromMillis(0, { zone: "utc" });

export function toSluggedId(value: string): SluggedId {
  return value as SluggedId;
}

export const holeBooleanMapSchema = z
  .object({
    "1": z.boolean(),
    "2": z.boolean(),
    "3": z.boolean(),
    "4": z.boolean(),
    "5": z.boolean(),
    "6": z.boolean(),
    "7": z.boolean(),
    "8": z.boolean(),
    "9": z.boolean(),
    "10": z.boolean(),
    "11": z.boolean(),
    "12": z.boolean(),
    "13": z.boolean(),
    "14": z.boolean(),
    "15": z.boolean(),
    "16": z.boolean(),
    "17": z.boolean(),
    "18": z.boolean(),
  })
  .strict(); // Ensures no extra keys are present

const nameValidator = z
  .string()
  .max(100, { error: "Name must be 100 characters or fewer" });
const facilityIdValidator = z.string();
export const sluggedIdValidator = z
  .string()
  .regex(sluggedIdRegex, { error: "Invalid SluggedId format" })
  .transform((val) => val as SluggedId);
/*const isoDateStringValidator = //z.string()
//.refine((val) => !isNaN(Date.parse(val)), { message: "Must be a valid ISO date", })
//.transform((val) => val as IsoDateString)*/

const isoDateStringValidator = z
  .union([z.coerce.string(), z.coerce.date()])
  .transform((input, ctx) => {
    const str = input instanceof Date ? input.toISOString() : input;
    const dt = DateTime.fromISO(str, { zone: "utc" });
    if (!dt.isValid) {
      ctx.addIssue({
        code: "custom",
        message: `Invalid DateTime: ${dt.invalidExplanation}`,
        input: input,
      });
      return z.NEVER;
    }
    return dt;
  });

export const isoDateWithOffsetValidator = z
  .union([z.coerce.string(), z.coerce.date()])
  .transform((input, ctx) => {
    const str = input instanceof Date ? input.toISOString() : input;

    // Use fromISO without forcing UTC, so the timezone offset is preserved
    const dt = DateTime.fromISO(str);
    if (!dt.isValid) {
      ctx.addIssue({
        code: "custom",
        message: `Invalid DateTime: ${dt.invalidExplanation}`,
        input: input,
      });
      return z.NEVER;
    }

    // Return the DateTime object with the timezone offset
    return dt;
  });

const descriptionValidator = z
  .union([
    z
      .string()
      .max(1023, { error: "Description must be 1023 characters or fewer" }),
    z.null(),
  ])
  .optional();

export const descriptionSchema = {
  description: descriptionValidator,
};

export const nameSchema = {
  name: nameValidator,
};

export const facilityIdSchema = {
  facilityId: facilityIdValidator,
};

export const simulatorIdSchema = {
  simulatorId: facilityIdValidator,
};

export const idSchema = {
  id: facilityIdValidator,
};

export const sluggedIdSchema = {
  id: sluggedIdValidator,
};

export const createdSchema = {
  created: isoDateWithOffsetValidator,
};

export const lastModifiedSchema = {
  lastModified: isoDateStringValidator,
};

export const deletionSchema = z
  .object({
    deleted: z.boolean(),
    // Allow whenDeleted to be an IsoDateString or null, and it’s optional
    whenDeleted: z.union([isoDateStringValidator, z.null()]).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.deleted) {
      if (data.whenDeleted === undefined || data.whenDeleted === null) {
        ctx.addIssue({
          path: ["whenDeleted"],
          code: "custom",
          input: data,
          message: "whenDeleted must be provided when deleted is true",
        });
      }
    } else {
      if (data.whenDeleted !== undefined && data.whenDeleted !== null) {
        ctx.addIssue({
          path: ["whenDeleted"],
          code: "custom",
          input: data,
          message: "whenDeleted must not be provided when deleted is false",
        });
      }
    }
  });

export const problemDetailsSchema = z.looseObject({
  type: z.string().optional(),
  title: z.string().optional(),
  status: z.number().optional(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  extensions: z.record(z.string(), z.string()).optional(),
});
export const validationProblemDetailsSchema = problemDetailsSchema.extend({
  title: z.string(),
  errors: z.record(z.string(), z.array(z.string())),
});

type InternalIError = {
  reasons?: IError[];
  message?: string;
  metadata?: Record<string, unknown>;
};

export const errorSchema: z.ZodType<InternalIError> = z.lazy(() =>
  z.object({
    message: z.string(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    reasons: z.array(errorSchema).optional(),
  }),
);

/**
 * Response shape: { result: T, errors: IError[] }
 * T can be a single value or array depending on the schema passed in
 */
export const responseWithErrorsSchema = <TResponse>(
  resultSchema: z.ZodType<TResponse>,
) =>
  z.object({
    result: z.union([resultSchema, z.array(resultSchema)]),
    errors: z.array(errorSchema),
  });

export type ValidationProblemDetails = z.infer<
  typeof validationProblemDetailsSchema
>;
export type ProblemDetails = z.infer<typeof problemDetailsSchema>;
export type IError = z.infer<typeof errorSchema>;
export type ResponseWithErrors<TResponse> = z.infer<
  typeof responseWithErrorsSchema<TResponse>
>;
